import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, CircularProgress, IconButton, Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { QuotationService } from '../../services/quotation.service';
import { QuotationLineCreateDTO } from '../../interfaces/sales/quotation.interface';
import { CustomerService } from '../../services/customer.service';
import { Customer } from '../../interfaces/sales/customer.interface';
import { SalesSettingsService } from '../../services/salesSettings.service';
import { SalesSettings } from '../../interfaces/sales/salesSettings.interface';
import { AccountService } from '../../services/account.service';
import { helpWhy } from '../../help/why';
import { Account, Segment } from '../../interfaces/accounting.interface';
import { AccountAutocomplete } from '../common/AccountAutocomplete';
import { formatMoney } from '../../utils/format';
import { toLocalIsoDate } from '../../utils/dateUtils';
import { leafAccounts } from '../../utils/postingAccounts';
import { SegmentService } from '../../services/segment.service';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import ScrollableTable from '../common/ScrollableTable';
import SearchableSelect from '../common/SearchableSelect';
import SalespersonSelect from './scope/SalespersonSelect';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import TaxCodeSelect from '../tax/TaxCodeSelect';
import SalesCatalogueSelect from './SalesCatalogueSelect';   // FW-2A
import { discountError, discountPatch, previewLine, shownDiscountAmount } from '../../utils/salesLineDiscount';   // FW-2A
import { hasTaxCodes, previewTax, useTaxCodeLookup } from '../tax/useTaxCodeLookup';
import { TaxCodeLookup } from '../../interfaces/indirectTax.interface';
import DocumentCurrencyFields from '../common/DocumentCurrencyFields';   // G5b
import { catalogueUnitPrice, foreignLineDiscount, formatFc, isForeignCurrency, repriceCatalogueLines, toastSaved } from '../../utils/fcDocuments';   // G5b

const today = () => toLocalIsoDate();

const addDays = (dateStr: string, days: number): string => {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

// In-state row model: the DTO fields (quantity/unitPrice/taxRatePct may be NaN
// while the user is typing) plus a stable client-side key that is never sent
// to the server.
interface LineRow extends Omit<QuotationLineCreateDTO, 'quantity' | 'unitPrice' | 'taxRatePct'> {
  key: string;
  quantity: number;   // may be NaN while field is empty
  unitPrice: number;  // may be NaN while field is empty
  taxRatePct: number; // may be NaN while field is empty
  itemLabel?: string; // FW-2A
  catalogueBdtPrice?: number | null; // polish 4: set while the price still comes from the catalogue pick
}

// Helpers that treat NaN as 0 for live-total computation only.
const safeLineNet = (row: LineRow): number => previewLine(row).net;   // FW-2A: net of line discount
const safeLineTax = (row: LineRow): number => {
  if (row.taxCodeId) return previewTax(safeLineNet(row), Number.isFinite(row.taxRatePct) ? row.taxRatePct : 0);
  const t = Number.isFinite(row.taxRatePct) ? row.taxRatePct : 0;
  return +((safeLineNet(row) * t) / 100).toFixed(4);
};

const QuotationCreate: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);

  // Stable key counter — bumped each time a new row is created.
  const keyCounter = useRef(0);
  const nextKey = () => String(++keyCounter.current);

  // Header state
  const [customerId, setCustomerId] = useState('');
  const canPickSalesperson = usePermission(PERMISSIONS.SALES_REASSIGN);
  const [salespersonId, setSalespersonId] = useState<string | null>(null);
  const [quotationDate, setQuotationDate] = useState(today());
  const [validUntil, setValidUntil] = useState('');
  const [notes, setNotes] = useState('');
  // ---- G5b: quotation currency; the rate is indicative (the invoice resolves its own) ----
  const [currency, setCurrency] = useState('BDT');
  const [fxRate, setFxRate] = useState<number | ''>('');
  const foreign = isForeignCurrency(currency);
  // ---- end G5b ----

  // Reference data
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [revenueAccounts, setRevenueAccounts] = useState<Account[]>([]);
  const [segments, setSegments] = useState<Segment[]>([]);
  const [settings, setSettings] = useState<SalesSettings | null>(null);
  const defaultRevenueAcct = revenueAccounts.find(a => a.id === settings?.defaultRevenueAccountId);
  const defaultRevenueLabel = defaultRevenueAcct ? `Default: ${defaultRevenueAcct.name}` : 'Choose an account';

  // Lines — stored as LineRow[] (includes transient key)
  const [lines, setLines] = useState<LineRow[]>([]);

  const [validUntilTouched, setValidUntilTouched] = useState(false);

  const [loading, setLoading] = useState(true);
  const taxLookup = useTaxCodeLookup(AuthService.getSelectedInstitution());
  const showTaxCodes = hasTaxCodes(taxLookup);
  const defaultsApplied = useRef(false);
  const [saving, setSaving] = useState(false);

  const defaultCodeOf = (l: TaxCodeLookup | null) => {
    const cid = l?.defaultOutputCodeId;
    return (cid ? l?.codes?.find(x => x.id === cid && x.active) : undefined) ?? null;
  };

  const makeBlankLine = (s: SalesSettings | null): LineRow => {
    const dc = defaultCodeOf(taxLookup);
    return {
      key: nextKey(),
      description: '',
      quantity: 1,
      unitPrice: NaN,
      taxRatePct: dc ? dc.effectiveRatePct : (s?.defaultTaxRatePct ?? 0),
      taxCodeId: dc ? dc.id : null,
      revenueAccountId: undefined,
      segmentId: undefined,
    };
  };

  // New quotations only: the lookup can arrive after the first blank line exists; prefill it once.
  useEffect(() => {
    if (isEditMode || loading || !taxLookup || defaultsApplied.current) return;
    defaultsApplied.current = true;
    const dc = defaultCodeOf(taxLookup);
    if (!dc) return;
    setLines(prev => prev.map(l => (l.taxCodeId ? l : { ...l, taxCodeId: dc.id, taxRatePct: dc.effectiveRatePct })));
  }, [loading, taxLookup]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const institutionId = AuthService.getSelectedInstitution()!;
    const dataLoads: [
      Promise<any>,
      Promise<SalesSettings | null>,
      Promise<Account[]>,
      Promise<any>,
    ] = [
      CustomerService.listByInstitution(institutionId, 0, 200, isEditMode ? 'ALL' : undefined), // FW-1: edit keeps a now-inactive current customer
      SalesSettingsService.getByInstitution(institutionId).catch(() => null),
      AccountService.getAccountsByType('REVENUE'),
      SegmentService.getByInstitution(0, 200),
    ];

    if (isEditMode) {
      Promise.all([
        ...dataLoads,
        QuotationService.getById(id!),
      ])
        .then(([customerPage, salesSettings, revenueAccs, segmentPage, quotation]) => {
          setCustomers(customerPage.content);
          setSettings(salesSettings);

          const allAccounts: Account[] = Array.isArray(revenueAccs) ? revenueAccs : [];
          setRevenueAccounts(allAccounts);

          const allSegments: Segment[] = segmentPage.content ?? [];
          setSegments(allSegments.filter((s: Segment) => s.active !== false));

          // Prefill from existing quotation
          setCustomerId(quotation.customerId);
          setQuotationDate(quotation.quotationDate);
          setValidUntil(quotation.validUntil ?? '');
          setNotes(quotation.notes ?? '');
          const fc = isForeignCurrency(quotation.currencyCode);   // G5b: a foreign quotation is edited in its currency
          setCurrency(quotation.currencyCode ?? 'BDT');
          setFxRate(fc && quotation.exchangeRate != null ? quotation.exchangeRate : '');

          // Prefill lines with stable keys
          setLines(
            (quotation.lines ?? []).map((l: any) => ({
              key: nextKey(),
              description: l.description ?? '',
              quantity: l.quantity,
              unitPrice: fc && l.foreignUnitPrice != null ? l.foreignUnitPrice : l.unitPrice,
              taxRatePct: l.taxRatePct,
              taxCodeId: l.taxCodeId ?? null,
              revenueAccountId: l.revenueAccountId ?? undefined,
              segmentId: l.segmentId ?? undefined,
              itemId: l.itemId ?? null,   // FW-2A
              itemLabel: l.itemId ? 'Catalogue item' : undefined,
              discountPct: l.discountPct ?? null,
              discountAmount: l.discountPct != null ? null
                : fc ? (foreignLineDiscount(l.quantity, l.foreignUnitPrice, l.foreignAmount) || null)
                : (Number(l.discountAmount) > 0 ? l.discountAmount : null),
            }))
          );
        })
        .catch(e => toast.showApiError(e, 'Failed to load quotation data'))
        .finally(() => setLoading(false));
    } else {
      Promise.all(dataLoads)
        .then(([customerPage, salesSettings, revenueAccs, segmentPage]) => {
          setCustomers(customerPage.content);
          setSettings(salesSettings);

          const allAccounts: Account[] = Array.isArray(revenueAccs) ? revenueAccs : [];
          setRevenueAccounts(allAccounts);

          const allSegments: Segment[] = segmentPage.content ?? [];
          setSegments(allSegments.filter((s: Segment) => s.active !== false));

          // Prefill validUntil from settings if available
          if (salesSettings?.defaultQuotationValidityDays) {
            setValidUntil(addDays(today(), salesSettings.defaultQuotationValidityDays));
          }

          // Start with one blank line
          setLines([makeBlankLine(salesSettings)]);
        })
        .catch(e => toast.showApiError(e, 'Failed to load quotation data'))
        .finally(() => setLoading(false));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleQuotationDateChange = (newDate: string) => {
    setQuotationDate(newDate);
    // Re-derive validUntil from the new quotation date if settings provide a default
    // and user has not yet set a custom validUntil (only in create mode)
    if (!isEditMode && settings?.defaultQuotationValidityDays && !validUntilTouched) {
      setValidUntil(addDays(newDate, settings.defaultQuotationValidityDays));
    }
  };

  const handleAddLine = () => {
    setLines(prev => [...prev, makeBlankLine(settings)]);
  };

  const handleRemoveLine = (index: number) => {
    setLines(prev => prev.filter((_, i) => i !== index));
  };

  const handleLineChange = <K extends keyof LineRow>(
    index: number,
    field: K,
    value: LineRow[K]
  ) => {
    setLines(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  // Live totals — NaN fields treated as 0.
  const subtotal = lines.reduce((s, l) => s + safeLineNet(l), 0);
  const taxAmount = lines.reduce((s, l) => s + safeLineTax(l), 0);
  const total = subtotal + taxAmount;

  const isSubmitDisabled =
    saving ||
    (foreign && !(Number(fxRate) > 0)) ||
    !customerId ||
    lines.length === 0 ||
    lines.some(l => !(l.quantity > 0) || !(l.unitPrice > 0) || !!discountError(l));

  const submitWhy = saving ? null
    : !customerId ? 'Choose a customer first.'
    : lines.length === 0 ? 'Add at least one line.'
    : lines.some(l => !(l.quantity > 0) || !(l.unitPrice > 0)) ? 'Every line needs a quantity and a price above zero.'
    : lines.some(l => !!discountError(l)) ? 'A line discount must be between 0 and 100 percent and not more than the line amount.'
    : (foreign && !(Number(fxRate) > 0)) ? 'Enter an exchange rate above zero.'
    : null;

  const handleSubmit = async () => {
    if (!customerId) { toast.showApiError(null, 'Select a customer'); return; }
    if (lines.length === 0) { toast.showApiError(null, 'Add at least one line'); return; }
    if (lines.some(l => !(l.quantity > 0) || !(l.unitPrice > 0) || !!discountError(l))) {
      toast.showApiError(null, 'All lines must have a positive quantity and unit price');
      return;
    }
    if (foreign && !(Number(fxRate) > 0)) { toast.showApiError(null, `Enter the ${currency} rate`); return; }

    setSaving(true);
    try {
      const institutionId = AuthService.getSelectedInstitution()!;
      const dto = {
        customerId,
        ...(!isEditMode && canPickSalesperson && salespersonId ? { salespersonId } : {}),
        quotationDate,
        validUntil: validUntil || undefined,
        notes: notes || undefined,
        institutionId,
        currencyCode: currency, exchangeRate: foreign ? Number(fxRate) : null,   // G5b: BDT is sent too, so a foreign draft converts on purpose
        // Strip the transient key; coerce taxRatePct NaN→0 (qty/price are
        // finite because the submit guard above already checked > 0).
        lines: lines.map(l => ({
          description: l.description,
          quantity: l.quantity,
          unitPrice: l.unitPrice,
          taxRatePct: Number.isFinite(l.taxRatePct) ? l.taxRatePct : 0,
          taxCodeId: l.taxCodeId || null,
          revenueAccountId: l.revenueAccountId || undefined,
          segmentId: l.segmentId || undefined,
          itemId: l.itemId || undefined,   // FW-2A
          discountPct: l.discountPct ?? undefined,
          discountAmount: l.discountPct == null ? (l.discountAmount ?? undefined) : undefined,
        })),
      };

      if (isEditMode) {
        const updated = await QuotationService.update(id!, dto);
        toastSaved(toast, 'Quotation updated', updated?.warnings);   // G5b
        navigate(`/sales/quotations/${id}`);
      } else {
        const created = await QuotationService.create(dto);
        toastSaved(toast, 'Quotation created', created.warnings);   // G5b
        navigate(`/sales/quotations/${created.id}`);
      }
    } catch (e) {
      toast.showApiError(e, isEditMode ? 'Failed to update Quotation' : 'Failed to create Quotation');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 3 }} data-help-page="sales.quotationForm.page">
      <Typography variant="h5" sx={{ mb: 3 }}>
        {isEditMode ? 'Edit Quotation' : 'New Quotation'}
      </Typography>

      {/* Header */}
      <Paper data-help-section="sales.quotationForm.header" sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <SearchableSelect
            data-help="sales.quotationForm.customer"
            label="Customer"
            required
            options={customers}
            value={customerId}
            onChange={id => setCustomerId(id)}
            getOptionLabel={c => c.name}
          />

          {!isEditMode && canPickSalesperson && (
            <SalespersonSelect data-help="sales.quotationForm.field.salesperson"
              size="medium" value={salespersonId} onChange={setSalespersonId}
              helperText="Leave empty for the default owner (the customer's salesperson, else you)."
            />
          )}

          <TextField
            data-help="sales.quotationForm.date"
            label="Quotation Date"
            type="date"
            value={quotationDate}
            onChange={e => handleQuotationDateChange(e.target.value)}
            InputLabelProps={{ shrink: true }}
            required
          />

          <DocumentCurrencyFields institutionId={AuthService.getSelectedInstitution() ?? ''} currency={currency}
            currencyProps={{ 'data-help': 'sales.quotationForm.field.currency' }} rateProps={{ 'data-help': 'sales.quotationForm.field.rate' }}
            rate={fxRate} asOf={quotationDate} onChange={(c, r) => { setCurrency(c); setFxRate(r); setLines(prev => repriceCatalogueLines(prev, c, r)); }} />
          {foreign && (
            <Typography variant="caption" color="text.secondary">
              Prices are in {currency}; the rate is indicative: the invoice uses the rate of its own date.
            </Typography>
          )}

          <TextField
            data-help="sales.quotationForm.validUntil"
            label="Valid Until"
            type="date"
            value={validUntil}
            onChange={e => { setValidUntilTouched(true); setValidUntil(e.target.value); }}
            InputLabelProps={{ shrink: true }}
            helperText="Defaults to settings validity if left blank"
          />

          <TextField
            data-help="sales.quotationForm.notes"
            label="Notes"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            multiline
            minRows={3}
          />
        </Box>
      </Paper>

      {/* Lines */}
      <Paper data-help-section="sales.quotationForm.lines" sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h6">Quotation Lines</Typography>
          <Button data-help="sales.quotationForm.addLine" variant="outlined" startIcon={<Add />} onClick={handleAddLine}>
            Add Line
          </Button>
        </Box>

        {lines.length === 0 ? (
          <Typography color="text.secondary">No lines added yet.</Typography>
        ) : (
          <ScrollableTable>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell data-help="sales.quotationForm.col.product">Product / service</TableCell>
                    <TableCell data-help="sales.quotationForm.col.description">Description</TableCell>
                    <TableCell data-help="sales.quotationForm.col.quantity">Quantity</TableCell>
                    <TableCell data-help="sales.quotationForm.col.unitPrice">{foreign ? `Unit Price (${currency})` : 'Unit Price'}</TableCell>
                    <TableCell data-help="sales.quotationForm.col.discPct">Disc %</TableCell>
                    <TableCell data-help="sales.quotationForm.col.discAmount">{foreign ? `Disc amount (${currency})` : 'Disc amount'}</TableCell>
                    {showTaxCodes && <TableCell data-help="sales.quotationForm.col.taxCode">Tax code</TableCell>}
                    <TableCell data-help="sales.quotationForm.col.taxPct">Tax %</TableCell>
                    <TableCell data-help="sales.quotationForm.col.revenue">Revenue Account</TableCell>
                    <TableCell data-help="sales.quotationForm.col.segment">Segment</TableCell>
                    <TableCell align="right" data-help="sales.quotationForm.col.net">Net</TableCell>
                    <TableCell align="right" data-help="sales.quotationForm.col.tax">Tax</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lines.map((line, index) => (
                    <TableRow key={line.key}>
                      <TableCell sx={{ minWidth: 190 }}>
                        <SalesCatalogueSelect data-help="sales.quotationForm.col.product"
                          label="Product / service (optional)"
                          value={line.itemId ?? null}
                          valueLabel={line.itemLabel}
                          onChange={(id, item) => setLines(prev => prev.map((l, i) => (i !== index ? l : !id || !item
                            ? { ...l, itemId: null, itemLabel: undefined, catalogueBdtPrice: null }
                            : {
                              ...l,
                              itemId: id,
                              itemLabel: `${item.code} - ${item.name}`,
                              description: item.name,
                              unitPrice: item.salePrice != null ? catalogueUnitPrice(item.salePrice, currency, fxRate) : l.unitPrice,
                              catalogueBdtPrice: item.salePrice != null ? item.salePrice : null,   // polish 4
                              revenueAccountId: item.revenueAccountId || l.revenueAccountId,
                              ...(item.taxCodeId && taxLookup?.codes?.some(c => c.id === item.taxCodeId && c.active)
                                ? { taxCodeId: item.taxCodeId, taxRatePct: taxLookup.codes.find(c => c.id === item.taxCodeId)!.effectiveRatePct }
                                : {}),
                            })))}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField data-help="sales.quotationForm.col.description"
                          size="small"
                          value={line.description}
                          onChange={e => handleLineChange(index, 'description', e.target.value)}
                          placeholder="Description"
                          sx={{ minWidth: 160 }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField data-help="sales.quotationForm.col.quantity"
                          type="number"
                          size="small"
                          value={Number.isFinite(line.quantity) ? line.quantity : ''}
                          onChange={e => {
                            const v = e.target.value;
                            handleLineChange(index, 'quantity', v === '' ? NaN : Number(v));
                          }}
                          error={!(line.quantity > 0)}
                          inputProps={{ step: 'any', min: 0 }}
                          sx={{ width: 90 }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField data-help="sales.quotationForm.col.unitPrice"
                          type="number"
                          size="small"
                          value={Number.isFinite(line.unitPrice) ? line.unitPrice : ''}
                          onChange={e => {
                            const v = e.target.value;
                            setLines(prev => prev.map((l, i) => (i !== index ? l : { ...l, unitPrice: v === '' ? NaN : Number(v), catalogueBdtPrice: null })));
                          }}
                          error={!(line.unitPrice > 0)}
                          inputProps={{ step: 'any', min: 0 }}
                          sx={{ width: 110 }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField data-help="sales.quotationForm.col.discPct"
                          type="number" size="small"
                          inputProps={{ 'aria-label': 'Disc %', step: 'any', min: 0, max: 100 }}
                          value={line.discountPct ?? ''}
                          onChange={e => setLines(prev => prev.map((l, i) => (i !== index ? l : { ...l, ...discountPatch('pct', e.target.value) })))}
                          error={!!discountError(line)}
                          helperText={discountError(line) ?? undefined}
                          sx={{ width: 80 }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField data-help="sales.quotationForm.col.discAmount"
                          type="number" size="small"
                          inputProps={{ 'aria-label': 'Disc amount', step: 'any', min: 0 }}
                          value={shownDiscountAmount(line)}
                          onChange={e => setLines(prev => prev.map((l, i) => (i !== index ? l : { ...l, ...discountPatch('amount', e.target.value) })))}
                          sx={{ width: 100 }}
                        />
                      </TableCell>
                      {showTaxCodes && (
                        <TableCell>
                          <TaxCodeSelect data-help="sales.quotationForm.col.taxCode"
                            data-testid={`quo-taxcode-${index}`}
                            lookup={taxLookup}
                            side="OUTPUT"
                            value={line.taxCodeId}
                            onChange={(cid, item) => setLines(prev => prev.map((l, i) => (i !== index ? l : {
                              ...l,
                              taxCodeId: cid,
                              taxRatePct: item ? item.effectiveRatePct : (cid ? l.taxRatePct : (settings?.defaultTaxRatePct ?? 0)),
                            })))}
                          />
                        </TableCell>
                      )}
                      <TableCell>
                        <TextField data-help="sales.quotationForm.col.taxPct"
                          type="number"
                          size="small"
                          disabled={!!line.taxCodeId}
                          inputProps={{ 'aria-label': 'Tax %', step: 0.01, min: 0 }}
                          value={Number.isFinite(line.taxRatePct) ? line.taxRatePct : ''}
                          onChange={e => {
                            const v = e.target.value;
                            handleLineChange(index, 'taxRatePct', v === '' ? NaN : Number(v));
                          }}
                          sx={{ width: 80 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ minWidth: 180 }}>
                          <AccountAutocomplete data-help="sales.quotationForm.col.revenue"
                            accounts={leafAccounts(revenueAccounts)}
                            value={line.revenueAccountId ?? ''}
                            label="Revenue Account"
                            placeholder={defaultRevenueLabel}
                            onChange={accountId => {
                              handleLineChange(index, 'revenueAccountId', accountId || undefined);
                            }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <SearchableSelect data-help="sales.quotationForm.col.segment"
                            label="Segment"
                            size="small"
                            sx={{ minWidth: 160 }}
                            options={segments}
                            value={line.segmentId ?? ''}
                            onChange={val => handleLineChange(index, 'segmentId', val || undefined)}
                            getOptionLabel={s => `${s.code} — ${s.name}`}
                            placeholder="—"
                          />
                      </TableCell>
                      <TableCell align="right">{formatMoney(safeLineNet(line))}</TableCell>
                      <TableCell align="right">{formatMoney(safeLineTax(line))}</TableCell>
                      <TableCell>
                        <IconButton
                          data-help="sales.quotationForm.removeLine"
                          size="small"
                          color="error"
                          onClick={() => handleRemoveLine(index)}
                          aria-label="remove line"
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
          </ScrollableTable>
        )}
      </Paper>

      {/* Totals */}
      <Paper data-help-section="sales.quotationForm.totals" sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 4 }}>
          <Box>
            <Typography variant="body2" color="text.secondary">Subtotal</Typography>
            <Typography variant="body1">{foreign ? formatFc(currency, subtotal) : formatMoney(subtotal)}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Tax</Typography>
            <Typography variant="body1">{foreign ? formatFc(currency, taxAmount) : formatMoney(taxAmount)}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Total</Typography>
            <Typography variant="h6">{foreign ? formatFc(currency, total) : formatMoney(total)}</Typography>
          </Box>
        </Box>
      </Paper>

      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button
          data-help="sales.quotationForm.save"
          {...helpWhy(submitWhy ? { kind: 'status', message: submitWhy } : null)}
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitDisabled}
        >
          {saving ? <CircularProgress size={20} /> : isEditMode ? 'Update Quotation' : 'Create Quotation'}
        </Button>
        <Button data-help="sales.quotationForm.cancel" variant="outlined" onClick={() => navigate(-1)} disabled={saving}>
          Cancel
        </Button>
      </Box>
    </Box>
  );
};

export default QuotationCreate;
